import { ReactComponent as DetailsIcon } from "assets/icons/details.svg";
import { DEFAULT_PAGE_SIZE_OPTION } from "helpers/PaginationHelper";
import { CustomTable, OptionType, Pagination, TableType } from "modules/common";
import { useMemo } from "react";
import { SortingRule } from "react-table";
import { MobileApp, MobileAppPaginatedResponse } from "services/mobileAppsPortal/response";
import { getDisplayDateFormat } from "helpers/DatesHelper";
import { classNames } from "helpers/ClassNameHelper";

export interface IMobileAppsTable {
    mobileApps?: MobileAppPaginatedResponse;
    isLoading?: boolean;
    setSortBy?: (sortBy: SortingRule<object>) => void;
    page?: number;
    pageSize?: number;
    onPageSizeChange?: (value: number) => void;
    onPageChange?: (page: number) => void;
    onRowSelect?: (row: MobileApp) => void;
    className?: string;
}

const MAX_URL_LENGTH = 20;

const defaultTableColumns: any[] = [
    {
        Header: "NAZIV",
        accessor: "name",
        Cell: ({ row }: any) => {
            const { original } = row;
            return <span className="modal-name">{original.name}</span>;
        },
    },
    {
        Header: "OPIS",
        accessor: "description",
    },
    {
        Header: "ANDROID APLIKACIJA",
        accessor: "linkAndroid",
        Cell: ({ row }: any) => {
            const { original } = row;
            const elipsis =
                original.linkAndroid && original.linkAndroid.length > MAX_URL_LENGTH ? "..." : "";
            return (
                <a className="email-link-cell" href={original.linkAndroid}>
                    {original.linkAndroid
                        ? `${original.linkAndroid.slice(0, MAX_URL_LENGTH)}${elipsis}`
                        : ""}
                </a>
            );
        },
    },
    {
        Header: "VERZIJA",
        accessor: "versionAndroid",
    },
    {
        Header: "iOS APLIKACIJA",
        accessor: "linkIOS",
        Cell: ({ row }: any) => {
            const { original } = row;
            const elipsis =
                original.linkIOS && original.linkIOS.length > MAX_URL_LENGTH ? "..." : "";
            return (
                <a className="email-link-cell" href={original.linkIOS ?? "#"}>
                    {original.linkIOS
                        ? `${original.linkIOS.slice(0, MAX_URL_LENGTH)}${elipsis}`
                        : ""}
                </a>
            );
        },
    },
    {
        Header: "VERZIJA",
        accessor: "versionIOS",
    },
    {
        Header: "EPOLJOPRIVREDA ID",
        accessor: "ePoljoprivredaId",
    },
    {
        Header: "DATUM KREIRANJA",
        accessor: "created",
        Cell: ({ row }: any) => {
            const { original } = row;
            return <span>{getDisplayDateFormat(original.created)}</span>;
        },
    },
    {
        Header: "ZADNJA IZMJENA",
        accessor: "lastUpdated",
        Cell: ({ row }: any) => {
            const { original } = row;
            return <span>{getDisplayDateFormat(original.lastUpdated)}</span>;
        },
    },
];

export const MobileAppsTable = ({
    mobileApps,
    isLoading,
    setSortBy,
    page,
    pageSize,
    onPageSizeChange,
    onPageChange,
    onRowSelect,
    className,
}: IMobileAppsTable) => {
    const tableColumns = useMemo(() => {
        let columns = defaultTableColumns;
        if (!setSortBy) columns = columns.map(column => ({ ...column, disableSortBy: true }));
        if (onRowSelect) {
            const infoColumn = {
                id: "info",
                disableSortBy: true,
                className: "details-icon-cell",
                Cell: ({ row }: any) => {
                    return (
                        <DetailsIcon
                            className="details-icon"
                            onClick={onRowSelect && (() => onRowSelect(row.original))}
                        />
                    );
                },
            };

            columns = [...columns, infoColumn];
        }

        return columns;
    }, [defaultTableColumns]);

    const activePageSize = useMemo(() => {
        return pageSize
            ? { value: pageSize.toString(), label: pageSize.toString() }
            : DEFAULT_PAGE_SIZE_OPTION;
    }, [pageSize]);

    const classNameString = useMemo(() => classNames("mobile-apps-table", className), [className]);

    const handlePageSizeChange = (option: OptionType | null) => {
        onPageSizeChange &&
            onPageSizeChange(parseInt(option?.value ?? DEFAULT_PAGE_SIZE_OPTION.value));
    };

    const handlePageClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        const target = event.currentTarget ?? event.target;
        onPageChange && onPageChange(Number(parseInt(target.id)));
    };

    const handleNexPageClick = () => {
        onPageChange && onPageChange(page ? page + 1 : 1);
    };

    const handlePrevPageClick = () => {
        onPageChange && onPageChange(page ? page - 1 : 1);
    };

    const handlePageSelect = (page: number) => {
        onPageChange && onPageChange(page);
    };

    const handleFirstPageClick = () => {
        onPageChange && onPageChange(1);
    };

    const handleLastPageClick = () => {
        if (mobileApps?.items && mobileApps?.items.length > 0)
            onPageChange &&
                onPageChange(Math.ceil(mobileApps.total / parseInt(activePageSize.value)));
    };
    /* TO DO: For pagination add handleDelete method to remove onPageChange checks and instead handle page changes in the parent component (throughout the app)*/
    return (
        <div className={classNameString}>
            <CustomTable
                data={mobileApps?.items ?? []}
                columns={tableColumns}
                loading={isLoading}
                setSortBy={setSortBy}
                tableType={TableType.AdministrationTable}
                flex
            />

            {mobileApps && mobileApps.items?.length > 0 && (
                <Pagination
                    className="mt-24"
                    currentPage={page ?? 1}
                    numberOfPages={Math.ceil(mobileApps.total / parseInt(activePageSize.value))}
                    pageNeighbours={1}
                    pageSize={activePageSize}
                    onPageSizeChange={handlePageSizeChange}
                    handlePageClick={handlePageClick}
                    handlePageSelect={handlePageSelect}
                    next={handleNexPageClick}
                    prev={handlePrevPageClick}
                    first={handleFirstPageClick}
                    last={handleLastPageClick}
                />
            )}
        </div>
    );
};
